import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { AdTopBanner, AdInContent, AdBottomBanner } from '../components/AdSense';

const BodyFatCalculatorPage = () => {
  const [unitSystem, setUnitSystem] = useState('us');
  const [gender, setGender] = useState('male');
  const [age, setAge] = useState('25');
  const [weightLbs, setWeightLbs] = useState('180');
  const [weightKg, setWeightKg] = useState('82');
  const [heightFeet, setHeightFeet] = useState('5');
  const [heightInches, setHeightInches] = useState('10');
  const [heightCm, setHeightCm] = useState('178');
  const [neckInches, setNeckInches] = useState('15');
  const [neckCm, setNeckCm] = useState('38');
  const [waistInches, setWaistInches] = useState('34');
  const [waistCm, setWaistCm] = useState('86');
  const [hipInches, setHipInches] = useState('40');
  const [hipCm, setHipCm] = useState('102');
  const [result, setResult] = useState(null);

  const calculate = () => {
    let heightCmVal, waistCmVal, neckCmVal, hipCmVal, weightKgVal;

    if (unitSystem === 'us') {
      heightCmVal = (parseFloat(heightFeet) * 12 + parseFloat(heightInches)) * 2.54;
      waistCmVal = parseFloat(waistInches) * 2.54;
      neckCmVal = parseFloat(neckInches) * 2.54;
      hipCmVal = parseFloat(hipInches) * 2.54;
      weightKgVal = parseFloat(weightLbs) * 0.453592;
    } else {
      heightCmVal = parseFloat(heightCm);
      waistCmVal = parseFloat(waistCm);
      neckCmVal = parseFloat(neckCm);
      hipCmVal = parseFloat(hipCm);
      weightKgVal = parseFloat(weightKg);
    }

    let bodyFat;
    if (gender === 'male') {
      bodyFat = 495 / (1.0324 - 0.19077 * Math.log10(waistCmVal - neckCmVal) + 0.15456 * Math.log10(heightCmVal)) - 450;
    } else {
      bodyFat = 495 / (1.29579 - 0.35004 * Math.log10(waistCmVal + hipCmVal - neckCmVal) + 0.22100 * Math.log10(heightCmVal)) - 450;
    }

    bodyFat = Math.max(0, Math.min(100, bodyFat));
    const fatMass = (bodyFat / 100) * weightKgVal;
    const leanMass = weightKgVal - fatMass;

    let category;
    if (gender === 'male') {
      if (bodyFat < 6) category = 'Essential Fat';
      else if (bodyFat < 14) category = 'Athletes';
      else if (bodyFat < 18) category = 'Fitness';
      else if (bodyFat < 25) category = 'Average';
      else category = 'Obese';
    } else {
      if (bodyFat < 14) category = 'Essential Fat';
      else if (bodyFat < 21) category = 'Athletes';
      else if (bodyFat < 25) category = 'Fitness';
      else if (bodyFat < 32) category = 'Average';
      else category = 'Obese';
    }

    setResult({
      bodyFat: bodyFat.toFixed(1),
      fatMass: unitSystem === 'us' ? (fatMass * 2.20462).toFixed(1) : fatMass.toFixed(1),
      leanMass: unitSystem === 'us' ? (leanMass * 2.20462).toFixed(1) : leanMass.toFixed(1),
      category,
      unit: unitSystem === 'us' ? 'lbs' : 'kg'
    });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <AdTopBanner />
          
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Body Fat Calculator</span>
          </nav>

          <h1 className="text-3xl font-bold text-white mb-4">Body Fat Calculator</h1>
          <p className="text-gray-400 mb-6">Estimate your body fat percentage using the U.S. Navy Method.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="flex border-b border-[#2a2a2a] mb-6">
              <button onClick={() => setUnitSystem('us')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${unitSystem === 'us' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                US Units
              </button>
              <button onClick={() => setUnitSystem('metric')}
                className={`px-4 py-2 font-medium text-sm border-b-2 ${unitSystem === 'metric' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400'}`}>
                Metric Units
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-20">Gender</label>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={gender === 'male'} onChange={() => setGender('male')} className="accent-cyan-500" />
                      <span className="text-gray-300">Male</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="radio" checked={gender === 'female'} onChange={() => setGender('female')} className="accent-cyan-500" />
                      <span className="text-gray-300">Female</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <label className="text-gray-300 w-20">Age</label>
                  <Input type="number" value={age} onChange={(e) => setAge(e.target.value)}
                    className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                </div>
                {unitSystem === 'us' ? (
                  <>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Weight</label>
                      <Input type="number" value={weightLbs} onChange={(e) => setWeightLbs(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">lbs</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Height</label>
                      <Input type="number" value={heightFeet} onChange={(e) => setHeightFeet(e.target.value)}
                        className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">ft</span>
                      <Input type="number" value={heightInches} onChange={(e) => setHeightInches(e.target.value)}
                        className="w-16 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">in</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Weight</label>
                      <Input type="number" value={weightKg} onChange={(e) => setWeightKg(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">kg</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Height</label>
                      <Input type="number" value={heightCm} onChange={(e) => setHeightCm(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">cm</span>
                    </div>
                  </>
                )}
              </div>
              <div className="space-y-4">
                {unitSystem === 'us' ? (
                  <>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Neck</label>
                      <Input type="number" value={neckInches} onChange={(e) => setNeckInches(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">inches</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Waist</label>
                      <Input type="number" value={waistInches} onChange={(e) => setWaistInches(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">inches</span>
                    </div>
                    {gender === 'female' && (
                      <div className="flex items-center gap-4">
                        <label className="text-gray-300 w-20">Hip</label>
                        <Input type="number" value={hipInches} onChange={(e) => setHipInches(e.target.value)}
                          className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                        <span className="text-gray-400">inches</span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Neck</label>
                      <Input type="number" value={neckCm} onChange={(e) => setNeckCm(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">cm</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="text-gray-300 w-20">Waist</label>
                      <Input type="number" value={waistCm} onChange={(e) => setWaistCm(e.target.value)}
                        className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                      <span className="text-gray-400">cm</span>
                    </div>
                    {gender === 'female' && (
                      <div className="flex items-center gap-4">
                        <label className="text-gray-300 w-20">Hip</label>
                        <Input type="number" value={hipCm} onChange={(e) => setHipCm(e.target.value)}
                          className="w-24 bg-[#0d0d0d] border-[#2a2a2a] text-white" />
                        <span className="text-gray-400">cm</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <Button onClick={calculate} className="mt-6 bg-cyan-600 hover:bg-cyan-500 text-white px-8">Calculate</Button>

            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="text-center mb-6">
                  <p className="text-gray-400 mb-2">Body Fat Percentage</p>
                  <p className="text-5xl font-bold text-green-400">{result.bodyFat}%</p>
                  <p className="text-cyan-400 mt-2">{result.category}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Fat Mass</p>
                    <p className="text-white font-bold">{result.fatMass} {result.unit}</p>
                  </div>
                  <div className="p-3 bg-[#1a1a1a] rounded-lg text-center">
                    <p className="text-gray-400 text-sm">Lean Mass</p>
                    <p className="text-white font-bold">{result.leanMass} {result.unit}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          <AdInContent />

          <div className="mt-10 p-6 bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl">
            <h3 className="text-lg font-semibold text-white mb-4">Fitness & Health Calculators</h3>
            <div className="flex flex-wrap gap-2">
              {[{ name: 'BMI', path: '/bmi-calculator' }, { name: 'BMR', path: '/bmr-calculator' }, { name: 'Calorie', path: '/calorie-calculator' }, { name: 'Ideal Weight', path: '/ideal-weight-calculator' }].map((calc) => (
                <Link key={calc.path} to={calc.path} className="px-3 py-1.5 bg-[#0d0d0d] border border-[#2a2a2a] rounded text-cyan-400 hover:text-cyan-300 text-sm">{calc.name}</Link>
              ))}
            </div>
          </div>
          <AdBottomBanner />
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BodyFatCalculatorPage;
